import React, { useEffect, useMemo, useRef, useContext } from 'react';

import config from 'config';
import { useRouter } from 'next/router';
import PropTypes from 'prop-types';
import { useDispatch } from 'react-redux';
import styled, { css } from 'styled-components';

import BuilderCollectionContext from '~/builder/components/BuilderCollectionContext';
import { useProductGridRelatedProductsContext } from '~/grid/context/ProductGridRelatedProductsContext';
import LoyaltyLockIcon from '~/icons/components/LoyaltyLockIcon/LoyaltyLockIcon';
import WhitelabelIcon from '~/icons/components/WhitelabelIcon';
import ProductCallout from '~/shared/components/ProductCallout';
import ProductCellPriceDetails from '~/shared/components/ProductCellPriceDetails';
import WishlistToggleButton from '~/shared/components/WishlistToggleButton';
import featureNames from '~/shared/constants/featureFlags';
import { LoyaltyTiers } from '~/shared/constants/loyaltyTiers';
import { ProductTag } from '~/shared/constants/products';
import generateSxfCdnImageUrl from '~/shared/utils/generateSxfCdnImageUrl';
import getCartPrice from '~/shared/utils/getCartPrice';
import { isLockedToMember } from '~/shared/utils/loyalty';
import { navigateToPathnameFromHref } from '~/shared/utils/navigateToPathnameFromHref';
import useHasFeature from '~/shared/utils/useHasFeature';
import useProductCallout, { Callout } from '~/shared/utils/useProductCallout';
import useSecondarySite from '~/shared/utils/useSecondarySite';
import { Link } from '~/techstyle-shared/next-routes';
import { useLoyalty, useMembership } from '~/techstyle-shared/react-accounts';
import { useCart } from '~/techstyle-shared/react-cart';
import {
  ProductCell as BaseProductCell,
  ProductImage,
  ProductName as BaseProductName,
  ProductReview,
  useTabIndex,
  GridColorSwatches,
  useBreakpoint,
} from '~/techstyle-shared/react-components';
import { useIntl } from '~/techstyle-shared/react-intl';
import {
  ProductType,
  trackProductClicked,
  useProductListViewed,
} from '~/techstyle-shared/react-products';
import { buildURL, useSession } from '~/techstyle-shared/redux-core';

import GridSwatches from '../GridSwatches';
import SwatchesLoadingSkeleton from '../SwatchesLoadingSkeleton';

const ProductLinkInner = styled.a`
  display: block;
  width: 100%;

  &:empty {
    display: none;
  }
`;

const ProductLink = ({ href, ...otherProps }) => {
  return (
    <Link href={href}>
      <ProductLinkInner {...otherProps} />
    </Link>
  );
};
ProductLink.propTypes = {
  href: PropTypes.string.isRequired,
};

const Wrapper = styled(BaseProductCell)`
  ${({ theme }) => theme.gridCellStyles.productCellWrapper}
`;

const ImageOverlay = styled.div`
  position: absolute;
  top: 8px;
  right: 8px;
`;

const LoyaltyLockBackground = styled.div`
  position: absolute;
  border-radius: 50%;
  width: 30px;
  height: 30px;
  background-color: black;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  display: flex;
  justify-content: center;
  align-items: center;
`;

const StyledLoyaltyLockIcon = styled(LoyaltyLockIcon)`
  margin-top: -1px;
`;

const ProductSection = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  margin: 8px 0;

  /**
    * temp solution (approved by UAT) to minimize content shift.
    * will be replaced with a better solution per ticket below
    * https://techstyle-prod.atlassian.net/browse/FLD-1747
    */
  min-height: 140px;
`;

const FavoritesPlaceholderImage = styled.div`
  background-color: ${({ theme }) =>
    theme.colors.favoritesPlaceholderImageBackground};
  display: block;
  height: 225.14px;
  width: 150px;
  @media (max-width: 768px) {
    height: 354px !important;
    min-width: 100%;
  }
  @media (max-width: 540px) {
    height: 392px !important;
    width: 100%;
  }
  @media (max-width: 414px) {
    height: 297px !important;
    width: 100%;
  }
  @media (max-width: 411px) {
    height: 295px !important;
    width: 100%;
  }
  @media (max-width: 400px) {
    height: 267.92px !important;
    width: 100%;
  }
  @media (max-width: 360px) {
    height: 257px !important;
  }
  @media (max-width: 320px) {
    height: 227px !important;
  }
  @media (max-width: 280px) {
    height: 225px !important;
  }
`;

const ProductName = styled(BaseProductName)`
  display: block;
  ${({ theme }) => theme.gridCellStyles.productName}
  ${({ $isNewPricingUiEnabled, $isMobile }) =>
    $isNewPricingUiEnabled &&
    css`
      margin: 0;
      display: flex;
      flex-direction: column;
      align-items: flex-start;
      gap: 12px;
      align-self: stretch;

      display: -webkit-box;
      -webkit-box-orient: vertical;
      -webkit-line-clamp: 2;
      align-self: stretch;

      overflow: hidden;
      color: ${({ theme }) => theme.colors.pricingUiTestBoldLabel};
      text-overflow: ellipsis;
      font-family: ${({ theme }) => theme.fontStacks.standardGtPressuraFamily};
      font-size: ${$isMobile ? '12px' : '14px'};
      font-style: normal;
      font-weight: 350;
      line-height: 16px; /* 114.286% */
      padding-top: 8px;
    `};
`;

const ratingStyle = css`
  color: ${({ theme }) => theme.colors.textEmphasized};
  & svg {
    width: 12px;
  }
`;

const priceStyle = css`
  ${({ theme }) => theme.gridCellStyles.priceStyles}
`;

const toggleButtonStyle = css`
  padding: 6px;
`;

const ImageProductLink = styled(ProductLink)`
  position: relative;
`;

const StyledProductImage = styled(ProductImage)`
  ${(props) =>
    props.$exclusiveLocked &&
    `
    filter: contrast(30%) brightness(150%);
  `}
`;

/**
 * This is used to wrap specific callouts like "Sold Out" or "Coming Soon"
 * Currently only used for SXF at the moment.
 */
const CalloutOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${(props) => props.theme.colors.semanticColors.lightShadow2};
  pointer-events: none;
`;

/**
 * This is currently only used by SXF callouts that use the `CalloutOverlay`
 * (e.g. "Sold Out" or "Coming Soon")
 */
const CalloutOverlayText = styled(ProductCallout)`
  ${({ theme }) => theme.typography.values.paragraph2};
  color: ${(props) => props.theme.colors.palette.white};
`;

const generateSxfFallbackImages = (product) => {
  const defaultSet = ['1', '2', '3', '4', 'LAYDOWN'].map((angle) => {
    const sizes = [6, 7, 8, 9].map((imageSize) => {
      return generateSxfCdnImageUrl({
        product,
        angle,
        withWidthHeight: true,
        imageSize,
      });
    });
    return { angle, sizes };
  });
  return {
    default: defaultSet,
    plus: [],
  };
};

const DEFAULT_GRID_PRICING = {};
const useSavageCalloutDisplay = config.get(
  'public.brand.features.useSavageCalloutDisplay'
);
const useSavageProductImages = config.get(
  'public.brand.features.useSavageProductImages'
);

const ProductCell = React.forwardRef(
  (
    {
      product: productFromProps,
      productPricingFromGridContext = DEFAULT_GRID_PRICING, // used for getting data-cnstrc-item-price in ProductGrid view (ex. search result page)
      favorites,
      imageAspectRatio,
      imageType,
      productSource,
      href,
      reviewsHref,
      additionalContentHref,
      tabIndex,
      gridIndex,
      fplCode,
      fplId,
      payloadKey,
      showColorsLink = false,
      showGridReviews = false,
      swatchData,
      ...rest
    },
    ref
  ) => {
    const { isMobile } = useBreakpoint();

    const router = useRouter();
    const { isVip } = useMembership();
    tabIndex = useTabIndex(tabIndex);
    const { formatMessage } = useIntl();
    const gridColorSwatchesContext = GridColorSwatches.useContext();
    const { isVipCart } = useCart({ isLoginRequired: true }) || {};
    const { enabled: isNewPricingUiEnabled } = useHasFeature(
      featureNames.PRICING_UI_AB
    );

    const { customFields: { gridGender } = {} } =
      useContext(BuilderCollectionContext) || {};

    const secondarySite = useSecondarySite();
    const routePrefix = secondarySite ? `/${secondarySite}` : '';

    const isBundle = productFromProps.productTypeId === ProductType.BUNDLE;
    const showColorSwatches =
      gridColorSwatchesContext &&
      gridColorSwatchesContext.activeProduct &&
      !isBundle &&
      !secondarySite;

    const productAutoTag = isBundle ? { 'data-autotag': 'bundle_product' } : {};

    const product = useMemo(() => {
      if (showColorSwatches) {
        return gridColorSwatchesContext.activeProduct;
      } else {
        return productFromProps;
      }
    }, [gridColorSwatchesContext, productFromProps, showColorSwatches]);

    const { isLoading: isExtendedProductDataLoaded } =
      useProductGridRelatedProductsContext() || {};

    const callout = useProductCallout({ product, isGridCallout: true });
    // We only want to show the callout overlay
    const isCalloutWrapperEnabled = useMemo(() => {
      if (callout && useSavageCalloutDisplay) {
        return [Callout.COMING_SOON, Callout.SOLD_OUT].includes(callout.type);
      }

      return false;
    }, [callout]);

    const { isLoggedIn } = useSession();
    const dispatch = useDispatch();
    const { tier } = useLoyalty();

    /* we ran into an issue where the CIO api response for product.tagIdList contains strings only,
    whereas ProductTag.platinumExclusive from the configs is being interpreted as a number.
    In order to ensure isProductPlatinumExclusive is returning the proper value,
    We need to check both cases, since different API endpoints such as V2, GQL, and CIO
    may return tag IDs in different formats */
    const isProductPlatinumExclusive =
      product?.tagIdList?.includes(String(ProductTag.platinumExclusive)) ||
      product?.tagIdList?.includes(Number(ProductTag.platinumExclusive));
    const isLoyaltyLocked =
      isProductPlatinumExclusive &&
      isLockedToMember(tier?.label, LoyaltyTiers.PLATINUM);
    const isMysteryBox = product?.isMysteryBundle;
    if (useSavageProductImages) {
      if (!product.productImages && product.product_images) {
        product.productImages = product.product_images;
      }

      if (
        product?.productImages?.default?.length === 0 &&
        product?.productImages?.plus?.length === 0
      ) {
        product.productImages = generateSxfFallbackImages(product);
      }
    }

    if (!href) {
      href = buildURL(
        `${routePrefix}/products/${
          product.permalink || product.masterProductId
        }`,
        {
          psrc: productSource,
          gridGender,
        }
      );
    }
    if (!reviewsHref) {
      reviewsHref = `${href}#reviews`;
    }

    const hasPromo =
      Boolean(product.promos?.promoCode) &&
      typeof product.promos?.promoCode === 'string';

    const productLabelDisplay = hasPromo
      ? formatMessage({
          id: product.promos.promoCode,
          defaultMessage: product.promos.promoCode,
        })
      : null;

    const productInfo = useMemo(
      () => ({
        product: {
          // This will add the pricing for Constructor grids, will be empty object outside of this context
          ...productPricingFromGridContext,
          ...product,
          promos: {
            ...product.promos,
            promoLabel: product.promos?.promoLabel || productLabelDisplay,
          },
        },
        href: href.split(/[#?]/)[0],
        gridIndex,
        fplId,
        fplCode,
        productSource,
        payloadKey,
      }),
      [
        product,
        productLabelDisplay,
        href,
        gridIndex,
        fplId,
        fplCode,
        productSource,
        payloadKey,
        productPricingFromGridContext,
      ]
    );

    const handleClick = (event) => {
      const { href } = event.currentTarget;
      const mpid = product?.masterProductId;
      const pageUrl = router.asPath;
      // Attempt to get the existing session storage item for the given URL
      const existingItem = sessionStorage.getItem(pageUrl);
      // Create a new scroll position object with the given mpid value
      const scrollPosition = { mpid };
      // If an item already exists, parse it and modify the mpid property
      if (existingItem) {
        const parsedItem = JSON.parse(existingItem);
        parsedItem.mpid = mpid; // Update the mpid property
        sessionStorage.setItem(pageUrl, JSON.stringify(parsedItem)); // Save the modified item
      } else {
        // If no item exists, store the new scroll position object in session storage
        sessionStorage.setItem(pageUrl, JSON.stringify(scrollPosition));
      }

      dispatch(
        trackProductClicked({
          ...productInfo,
          productUrl: href,
        })
      );
      // Without this check, users will get kicked out of the SPA
      if (!(event.metaKey || event.ctrlKey)) {
        event.preventDefault();
        const { href } = event.currentTarget;
        setTimeout(() => {
          navigateToPathnameFromHref(href);
        }, 300);
      }
    };

    const { observe, unobserve } = useProductListViewed();

    useEffect(() => {
      const node = productCellRef.current;
      // Quick way to check if the product has pricing info in Constructor grid,
      // this should always be available outside of contexts where pricing is fetched
      // from GraphQL. Without this, the pricing info can be null on Constructor grids
      // for the first render.
      const hasPricing = 'defaultUnitPrice' in productInfo.product;
      if (node && hasPricing && !isExtendedProductDataLoaded) {
        observe(node, productInfo);
        return () => unobserve(node);
      }
    }, [observe, unobserve, productInfo, isExtendedProductDataLoaded]);

    const productCellRef = useRef();
    const { reviewCount } = product;

    const constructorItemPrice =
      getCartPrice({ product, isVip, isVipCart, secondarySite }) ||
      getCartPrice({
        product: productPricingFromGridContext,
        isVip,
        isVipCart,
        secondarySite,
      });

    const constructorTags = {
      'data-cnstrc-item-id': product.masterProductId,
      'data-cnstrc-item-name': product.label,
      'data-cnstrc-item-price': constructorItemPrice,
    };

    return (
      <Wrapper
        {...rest}
        product={product}
        ref={productCellRef || ref}
        data-autotag={
          reviewCount > 0
            ? 'prodgrid-prod-with-rating'
            : 'pdp-modal-styledSelect-list'
        }
        data-mpid={product.masterProductId}
        {...constructorTags}
        data-testid="product-cell"
      >
        <ImageProductLink
          href={href}
          aria-label={product.label}
          tabIndex={tabIndex}
          onClick={handleClick}
          {...productAutoTag}
        >
          {favorites && !product.productImages ? (
            <FavoritesPlaceholderImage />
          ) : (
            <>
              <StyledProductImage
                aspectRatio={imageAspectRatio}
                type={imageType}
                aria-label={product.label}
                role="img"
                $exclusiveLocked={isLoyaltyLocked}
              />
              {isLoyaltyLocked && (
                <LoyaltyLockBackground>
                  <StyledLoyaltyLockIcon width={28} height={28} color="white" />
                </LoyaltyLockBackground>
              )}
            </>
          )}
          {isCalloutWrapperEnabled && (
            <CalloutOverlay>
              <CalloutOverlayText type={callout.type} value={callout.value} />
            </CalloutOverlay>
          )}
        </ImageProductLink>
        {isLoggedIn && !secondarySite && !isMysteryBox ? (
          <ImageOverlay>
            <WishlistToggleButton
              toggleButtonStyle={toggleButtonStyle}
              data-cnstrc-btn="add-to-favorites"
            />
          </ImageOverlay>
        ) : null}

        <ProductSection>
          {showColorSwatches &&
            (isExtendedProductDataLoaded ? (
              <SwatchesLoadingSkeleton
                primarySwatchUrl={productFromProps.swatchUrl}
              />
            ) : (
              <GridSwatches href={href} swatchData={swatchData} />
            ))}
          <ProductLink href={href} tabIndex={-1} onClick={handleClick}>
            <ProductName
              $isNewPricingUiEnabled={isNewPricingUiEnabled}
              $isMobile={isMobile}
            />
            <ProductCellPriceDetails
              product={productFromProps}
              favorites={favorites}
              priceStyle={priceStyle}
              isNewPricingUiEnabled={isNewPricingUiEnabled}
            />
          </ProductLink>
          <ProductLink
            href={reviewsHref}
            onClick={handleClick}
            tabIndex={reviewsHref === href ? -1 : tabIndex}
          >
            {showGridReviews && (
              <ProductReview
                ratingStyle={ratingStyle}
                hideReviewCount
                emptyIcon={<WhitelabelIcon iconName="emptyStar" />}
                fullIcon={<WhitelabelIcon iconName="filledStar" />}
              />
            )}
          </ProductLink>
        </ProductSection>
      </Wrapper>
    );
  }
);

ProductCell.displayName = 'ProductCell';

ProductCell.propTypes = {
  additionalContentHref: PropTypes.string,
  children: PropTypes.node,
  favorites: PropTypes.bool,
  fplCode: PropTypes.string,
  fplId: PropTypes.number,
  gridIndex: PropTypes.number,
  href: PropTypes.string,
  imageAspectRatio: PropTypes.number,
  imageType: PropTypes.oneOf(['default', 'plus']),
  payloadKey: PropTypes.string,
  product: PropTypes.object.isRequired,
  productPricingFromGridContext: PropTypes.object,
  productSource: PropTypes.string,
  reviewsHref: PropTypes.string,
  showColorsLink: PropTypes.bool,
  showGridReviews: PropTypes.bool,
  swatchData: PropTypes.array,
  tabIndex: PropTypes.number,
};

export default ProductCell;
